import { useState } from "react";
import { useParams, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CheckCircle, AlertCircle, Loader2, CreditCard } from "lucide-react";
import { format } from "date-fns";

interface InvoicePayData {
  invoiceNumber: string;
  periodStart: string;
  periodEnd: string;
  issuerName: string;
  balanceCents: number;
  platformFeeCents: number;
  stripeFeeCents: number;
  processingFeeCents: number;
  totalCents: number;
  status: string;
  alreadyPaid: boolean;
  onlineAvailable: boolean;
}

function fmt(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(d: string): string {
  try {
    return format(new Date(d), "MMM d, yyyy");
  } catch {
    return d;
  }
}

export default function InvoicePay() {
  const { token } = useParams<{ token: string }>();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const justPaid = params.get("paid") === "true";

  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const { data: invoice, isLoading, isError } = useQuery<InvoicePayData>({
    queryKey: ["invoice-pay", token],
    queryFn: () => apiRequest("GET", `/api/pay/invoice/${token}`).then(r => r.json()),
    enabled: !!token,
    retry: 1,
  });

  async function handlePay() {
    if (!invoice || !token) return;
    setPaying(true);
    setPayError(null);
    try {
      const res = await apiRequest("POST", `/api/pay/invoice/${token}`);
      const body = await res.json();
      if (!res.ok) {
        if (body.error === "already_paid") {
          setPayError("This invoice has already been paid.");
        } else {
          setPayError("Unable to start payment. Please try again.");
        }
        return;
      }
      if (body.url) {
        window.location.href = body.url;
      }
    } catch {
      setPayError("A network error occurred. Please try again.");
    } finally {
      setPaying(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-700 mb-2">Invoice not found</h2>
            <p className="text-sm text-gray-500">This link may have expired or is invalid.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const canPay = invoice.onlineAvailable && !invoice.alreadyPaid && invoice.status !== "paid";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="pb-2">
          <p className="text-sm text-gray-500 uppercase tracking-wide font-medium">Invoice</p>
          <CardTitle className="text-2xl font-bold">{invoice.invoiceNumber}</CardTitle>
          <p className="text-sm text-gray-500">
            From <span className="font-medium text-gray-700">{invoice.issuerName}</span>
          </p>
          <p className="text-sm text-gray-500">
            Period: {fmtDate(invoice.periodStart)} — {fmtDate(invoice.periodEnd)}
          </p>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Success banners */}
          {(justPaid || invoice.alreadyPaid) && (
            <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-sm font-medium text-green-800">
                {justPaid ? "Payment received. Thank you!" : "This invoice has already been paid."}
              </p>
            </div>
          )}

          {/* Fee breakdown — only when there's something to pay */}
          {!invoice.alreadyPaid && invoice.balanceCents > 0 && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-2 text-sm">
              <div className="flex justify-between text-gray-700">
                <span>Balance</span>
                <span>{fmt(invoice.balanceCents)}</span>
              </div>
              {invoice.processingFeeCents > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Processing fee</span>
                  <span>{fmt(invoice.processingFeeCents)}</span>
                </div>
              )}
              <Separator />
              <div className="flex justify-between font-semibold text-gray-900 text-base">
                <span>Total to pay</span>
                <span>{fmt(invoice.totalCents)}</span>
              </div>
            </div>
          )}

          {/* No online payment */}
          {!invoice.onlineAvailable && !invoice.alreadyPaid && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Online payment is not available for this invoice.
                Please contact <strong>{invoice.issuerName}</strong> to arrange payment.
              </p>
            </div>
          )}

          {/* Pay error */}
          {payError && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800">{payError}</p>
            </div>
          )}

          {/* Pay button */}
          {canPay && (
            <Button
              className="w-full h-12 text-base font-semibold bg-green-600 hover:bg-green-700"
              onClick={handlePay}
              disabled={paying}
            >
              {paying ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Pay {fmt(invoice.totalCents)} online
                </>
              )}
            </Button>
          )}

          <p className="text-xs text-center text-gray-400">
            Secure payment via Stripe. Your card is never stored by {invoice.issuerName}.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
